import React from "react";
import clsx from "clsx";
import {
  DestinationAuthSchema,
  DestinationAuthSchemaFieldType,
} from "../../../../../../../../models/Entities";
import { Text, TextField, TextFieldVariant } from "@merge-api/merge-javascript-shared";
import { isValidJSON, isValidHostname } from "../../../../../../../../services";

type CreateDestinationFormProps = {
  className?: string;
  authSchema: DestinationAuthSchema;
  authFields: { [key: string]: string };
  onAuthFieldsChange: (key: string, value: string) => void;
  symmetricDataKey: string | undefined;
  setSymmetricDataKey: React.Dispatch<React.SetStateAction<string | undefined>>;
};

const CreateDestinationForm = ({
  className,
  authSchema,
  authFields,
  onAuthFieldsChange,
  symmetricDataKey,
  setSymmetricDataKey,
}: CreateDestinationFormProps) => {
  return (
    <div className={clsx("w-full", className)}>
      {/* symmetric data key */}
      <div className="mb-6">
        <Text variant="h5">Add a Base64-encoded AES key to encrypt your credentials.</Text>
        <Text variant="sm">The AES key must be AES-256.</Text>
        <TextField
          variant={TextFieldVariant.Bordered}
          className="mt-4"
          placeholder="Enter symmetric data key"
          value={symmetricDataKey}
          onChange={(e) => setSymmetricDataKey(e.target.value)}
        />
      </div>

      {/* auth schema fields */}
      {authSchema.map(({ header, fields }) => (
        <div key={header} className="mb-6">
          <Text variant="h5">{header}</Text>
          {fields.map(({ key, placeholder, type }) => {
            const hasJsonError =
              type === DestinationAuthSchemaFieldType.JSON &&
              !isValidJSON(authFields[key]) &&
              !!authFields[key];

            const hasHostError =
              key === "host" && !!authFields[key] && !isValidHostname(authFields[key]);

            return (
              <TextField
                key={key}
                variant={TextFieldVariant.Bordered}
                className="mt-4"
                type={type === DestinationAuthSchemaFieldType.PASSWORD ? "password" : "text"}
                multiline={type === DestinationAuthSchemaFieldType.JSON}
                value={authFields[key]}
                placeholder={placeholder}
                onChange={(e) => onAuthFieldsChange(key, e.target.value)}
                error={hasJsonError || hasHostError}
                errorText={
                  hasHostError
                    ? "Please enter a valid hostname (e.g. 'my-db.us-east-1.rds.amazonaws.com'). Only letters, digits, hyphens, and dots are allowed."
                    : "Invalid JSON format. Please ensure your input is correctly structured as JSON."
                }
              />
            );
          })}
        </div>
      ))}

      {/* placeholder div to prevent overflow of bottom margin being hidden */}
      <div className="h-[1px]" />
    </div>
  );
};

export default CreateDestinationForm;
